import { Fragment, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Avatar, Badge, Box, ButtonBase, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip,
  Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import InsightsIcon from '@mui/icons-material/Insights';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import mark from '../assets/tx-mark.png';
import BrandLockup from '../components/brand/BrandLockup';
import useConnectionIssues from '../hooks/useConnectionIssues';
import useLogout from '../hooks/useLogout';
import useStoredState from '../hooks/useStoredState';
import initials from '../utils/initials';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../theme';

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/connections', label: 'Connections', icon: LinkIcon },
  { to: '/ad-accounts', label: 'Ad accounts', icon: AccountBalanceWalletIcon },
  { to: '/campaigns', label: 'Campaigns', icon: CampaignIcon },
  { to: '/adsets', label: 'Ad sets', icon: LayersIcon },
  { to: '/ads', label: 'Ads', icon: ViewQuiltIcon },
  { to: '/insights', label: 'Insights', icon: InsightsIcon },
];

const DRAWER_WIDTH = 280;

/**
 * Navigation in the sponsortrialx style: the TrialX logo on white, solid icons, the current page on a lavender pill
 * in the primary colour, and Logout at the end. Wide screens get a sidebar that collapses to an icon rail
 * (remembered in this browser); small screens a top bar whose menu button opens the same navigation in a drawer.
 */
export default function Sidebar({ session }) {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [collapsed, setCollapsed] = useStoredState('sidebar.collapsed', false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // red count on Connections while any connection is not working
  const issues = { '/connections': useConnectionIssues(Boolean(session?.is_authenticated)) };
  const issueCount = Object.values(issues).reduce((total, count) => total + (count || 0), 0);

  if (!wide) {
    return (
      <>
        <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, height: 56,
          bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0,
          zIndex: theme.zIndex.appBar }}>
          <Brand height={40} />
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open menu" edge="end">
            <Badge variant="dot" color="error" invisible={!issueCount}>
              <MenuIcon />
            </Badge>
          </IconButton>
        </Box>
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: DRAWER_WIDTH, bgcolor: 'sidebar.main' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: 64, px: 2.5, flexShrink: 0,
            bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <Brand />
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close menu" edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
          <NavItems session={session} issues={issues} onNavigate={() => setDrawerOpen(false)} />
          <SignedIn session={session} />
        </Drawer>
      </>
    );
  }

  return (
    // sticky at the window's height, so it stays put while the page scrolls; above the app header so the collapse
    // button on its edge is not covered
    <Box component="aside" sx={{
      position: 'sticky', top: 0, height: '100vh', alignSelf: 'flex-start', zIndex: theme.zIndex.appBar + 1,
      flexShrink: 0, display: 'flex', flexDirection: 'column',
      width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
      bgcolor: 'sidebar.main', borderRight: 1, borderColor: 'divider',
    }}>
      {/* the TrialX logo on white; collapsed, just the X */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
        height: 72, flexShrink: 0, px: collapsed ? 1 : 2.5, bgcolor: 'background.paper', borderBottom: 1,
        borderColor: 'divider', overflow: 'hidden' }}>
        {collapsed ? <Box component="img" src={mark} alt="TrialX" sx={{ height: 32, width: 32, display: 'block' }} />
          : <Brand />}
      </Box>

      <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
        <ButtonBase onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    sx={{ position: 'absolute', top: 22, right: -14, zIndex: 1, width: 28, height: 28,
                      borderRadius: '50%', bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                      color: 'text.secondary', boxShadow: '0 1px 3px rgba(37, 45, 82, 0.12)',
                      '&:hover': { color: 'primary.main', borderColor: alpha(theme.palette.primary.main, 0.4) } }}>
          {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
        </ButtonBase>
      </Tooltip>

      {/* the signed in user and account are in the app header on wide screens */}
      <NavItems session={session} issues={issues} collapsed={collapsed} />
    </Box>
  );
}

// The TrialX logo with "Social" fitted under TRIAL (BrandLockup)
function Brand({ height = 44 }) {
  return <BrandLockup height={height} />;
}

/**
 * The page links and Logout, stacked. collapsed shows icons only (labels in tooltips, the not working count on the
 * icon); onNavigate runs after a link is chosen (closes the mobile drawer).
 */
function NavItems({ session, issues, collapsed = false, onNavigate }) {
  const [loggingOut, logout] = useLogout();

  // sponsortrialx's spacing: roomy rows, 22px solid icons, 16px labels, a softly rounded pill
  const itemSx = {
    // flexGrow 0: ListItemButton grows by default, which stretched the rows over the sidebar's height
    flexGrow: 0, borderRadius: '10px', minHeight: 46, py: 1.25, px: 1.5, whiteSpace: 'nowrap', color: 'sidebar.text',
    justifyContent: collapsed ? 'center' : 'flex-start',
    '&:hover': { bgcolor: 'sidebar.hover' },
    '& .MuiListItemIcon-root': { minWidth: collapsed ? 0 : 38, color: 'sidebar.text' },
    '& .MuiSvgIcon-root': { fontSize: 22 },
  };
  // collapsed, the label moves into a tooltip
  const withTooltip = (label, node) => (collapsed ? <Tooltip title={label} placement="right">{node}</Tooltip> : node);

  return (
    <List component="nav" disablePadding sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.5,
      py: 2, overflowX: 'hidden', overflowY: 'auto', minHeight: 0 }}>
      {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => <Fragment key={to}>{withTooltip(label, (
        <ListItemButton component={NavLink} to={to} end={end} onClick={onNavigate}
                        aria-label={collapsed ? label : undefined}
                        sx={{
                          ...itemSx,
                          '&.active': {
                            bgcolor: 'sidebar.active', color: 'primary.main', fontWeight: 600,
                            '& .MuiListItemIcon-root': { color: 'primary.main' },
                          },
                        }}>
          <ListItemIcon>
            {/* collapsed, the not working count sits on the icon */}
            <Badge badgeContent={collapsed ? issues[to] : 0} color="error">
              <Icon fontSize="small" />
            </Badge>
          </ListItemIcon>
          {!collapsed && (
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: 16, fontWeight: 'inherit' }} />
          )}
          {!collapsed && issues[to] > 0 && (
            <Tooltip title={`${issues[to]} not working`}>
              <Badge badgeContent={issues[to]} color="error" sx={{ ml: 1.5, mr: 1 }}
                     aria-label={`${issues[to]} connections not working`} />
            </Tooltip>
          )}
        </ListItemButton>
      ))}</Fragment>)}
      {session?.is_authenticated && withTooltip('Logout', (
        <ListItemButton onClick={logout} disabled={loggingOut} sx={itemSx}
                        aria-label={collapsed ? 'Logout' : undefined}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          {!collapsed && (
            <ListItemText primary={loggingOut ? 'Logging out…' : 'Logout'}
                          primaryTypographyProps={{ fontSize: 16 }} />
          )}
        </ListItemButton>
      ))}
    </List>
  );
}

// The drawer's footer on small screens, where there is no app header: the user, their role and the account
function SignedIn({ session }) {
  if (!session?.user) return null;
  const { user, account } = session;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.5, borderTop: 1,
      borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Avatar sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 600, color: 'primary.main',
        bgcolor: 'sidebar.active' }}>
        {initials(user.name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{user.name}</Typography>
        <Typography noWrap variant="caption" color="text.secondary" component="div">
          {[user.role, account?.name].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
    </Box>
  );
}
